import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight, FileText, Search } from "lucide-react";
import { Logo } from "@/components/Logo";
import { TopoTexture } from "@/components/TopoTexture";
import { SummaryFigure, SummaryFigures } from "@/components/shared/SummaryFigures";
import {
  AnalyticsIcon,
  BudgetIcon,
  ClientIcon,
  InvoiceIcon,
  MatchIcon,
  ReceiptIcon,
  SettingsIcon,
  TemplateIcon,
  TransactionsIcon,
} from "@/components/icons/NavIcons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { makeTr, type LandingLocale, pick } from "@/lib/landing/locale";
import { MonthTileMock, StatusBar, StatusChip, type MockTx } from "./mock";
import { ROW_TINT, STATUSES, type LandingStatus } from "./statuses";

/*
 * A faithful mock of the app: the sidebar and three views (month, year,
 * analytics), drawn with the app's classes and tokens. All data is invented.
 */

type View = "month" | "year" | "analytics";

const MONTHS = {
  cs: [
    "Leden",
    "Únor",
    "Březen",
    "Duben",
    "Květen",
    "Červen",
    "Červenec",
    "Srpen",
    "Září",
    "Říjen",
    "Listopad",
    "Prosinec",
  ],
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};
const MONTHS_SHORT = {
  cs: ["Led", "Úno", "Bře", "Dub", "Kvě", "Čer", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

const SEPTEMBER: MockTx[] = [
  {
    date: "28. 9.",
    text: { cs: "Poplatek za vedení účtu", en: "Account fee" },
    amount: "−149,00 Kč",
    status: "notNeeded",
  },
  {
    date: "28. 9.",
    text: { cs: "Platba kartou: Papírnictví Kotva", en: "Card: Papírnictví Kotva" },
    amount: "−1 249,00 Kč",
    status: "matched",
    doc: "Papírnictví Kotva",
  },
  {
    date: "27. 9.",
    text: { cs: "Platba kartou: Kavárna Na Rohu", en: "Card: Kavárna Na Rohu" },
    amount: "−312,00 Kč",
    status: "matched",
    doc: "Kavárna Na Rohu",
  },
  {
    date: "26. 9.",
    text: { cs: "Nájem kanceláře", en: "Office rent" },
    amount: "−18 500,00 Kč",
    status: "missing",
  },
  {
    date: "25. 9.",
    text: { cs: "Příchozí platba: Studio Lípa", en: "Incoming: Studio Lípa" },
    amount: "48 400,00 Kč",
    status: "invoice",
    doc: "FA 2026-018",
  },
  {
    date: "24. 9.",
    text: { cs: "Webhosting Oblak", en: "Oblak web hosting" },
    amount: "−349,00 Kč",
    status: "unmatched",
  },
  {
    date: "23. 9.",
    text: { cs: "Tiskárna Pixel", en: "Tiskárna Pixel" },
    amount: "−2 890,00 Kč",
    status: "matched",
    doc: "Tiskárna Pixel",
  },
];

const SEPT_COUNTS: Record<LandingStatus, number> = {
  matched: 13,
  invoice: 2,
  notNeeded: 1,
  missing: 1,
  unmatched: 3,
};

/** Ten months of an invented year: count, done, net, status counts. */
const YEAR: { count: number; net: string; c: [number, number, number, number, number] }[] = [
  { count: 18, net: "+38 410 Kč", c: [14, 2, 1, 0, 1] },
  { count: 21, net: "+41 905 Kč", c: [16, 2, 1, 1, 1] },
  { count: 22, net: "+29 760 Kč", c: [15, 3, 1, 1, 2] },
  { count: 17, net: "+52 118 Kč", c: [13, 3, 1, 0, 0] },
  { count: 19, net: "+44 302 Kč", c: [15, 3, 1, 0, 0] },
  { count: 20, net: "+31 655 Kč", c: [14, 2, 1, 1, 2] },
  { count: 16, net: "+12 940 Kč", c: [11, 2, 1, 1, 1] },
  { count: 23, net: "+47 220 Kč", c: [17, 3, 1, 1, 1] },
  { count: 20, net: "+12 355 Kč", c: [13, 2, 1, 1, 3] },
  { count: 12, net: "+8 760 Kč", c: [6, 1, 1, 1, 3] },
];

/** Income and expenses per month in thousands of crowns (Nov, Dec still to come). */
const TREND: [number, number][] = [
  [92, 54],
  [118, 76],
  [104, 74],
  [121, 69],
  [109, 65],
  [98, 66],
  [71, 58],
  [126, 79],
  [74, 62],
  [41, 32],
];

const KEYS: LandingStatus[] = ["matched", "invoice", "notNeeded", "missing", "unmatched"];
const toCounts = (c: number[]) =>
  Object.fromEntries(KEYS.map((k, i) => [k, c[i]])) as Record<LandingStatus, number>;

function Sidebar({ view, locale }: { view: View; locale: LandingLocale }) {
  const tr = makeTr(locale);
  const item = (Icon: typeof MatchIcon, label: string, active = false, badge?: number) => (
    <div
      key={label}
      className={cn(
        "flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px]",
        active ? "bg-primary/15 font-medium" : "text-foreground/80"
      )}
    >
      <Icon
        className={cn(
          "size-4",
          active ? "text-[hsl(var(--action))] dark:text-primary" : "text-muted-foreground"
        )}
      />
      <span className="flex-1">{label}</span>
      {badge !== undefined && (
        <span className="rounded-full bg-primary/20 px-1.5 text-[10px] font-semibold text-[hsl(var(--action))] dark:text-primary">
          {badge}
        </span>
      )}
    </div>
  );
  const group = (label: string) => (
    <p className="px-2 pb-1 pt-4 text-[11px] font-medium text-muted-foreground">{label}</p>
  );
  return (
    <div className="relative hidden w-48 shrink-0 flex-col border-r bg-[hsl(var(--sidebar-background))] p-2.5 md:flex">
      <TopoTexture
        className="inset-x-0 bottom-0 h-1/2 text-[hsl(var(--action))] opacity-[0.16] dark:text-foreground dark:opacity-[0.07]"
        fade="top"
      />
      <div className="relative px-2 pb-3 pt-1.5">
        <Logo size="sm" />
      </div>
      <div className="relative mb-3 rounded-lg border px-2.5 py-1.5">
        <p className="text-[12px] font-semibold">Moje firma s.r.o.</p>
        <p className="text-[10px] text-muted-foreground">
          {tr("Firemní finance", "Business finances")}
        </p>
      </div>
      <div className="relative space-y-0.5">
        {item(MatchIcon, tr("Párování", "Matching"), view !== "analytics", 4)}
        {item(TransactionsIcon, tr("Transakce", "Transactions"))}
        {item(ReceiptIcon, tr("Účtenky", "Receipts"))}
        {group(tr("Fakturace", "Invoicing"))}
        {item(InvoiceIcon, tr("Faktury", "Invoices"))}
        {item(ClientIcon, tr("Klienti", "Clients"))}
        {item(TemplateIcon, tr("Šablony", "Templates"))}
        {group(tr("Přehledy", "Reports"))}
        {item(AnalyticsIcon, tr("Analytika", "Analytics"), view === "analytics")}
        {item(BudgetIcon, tr("Rozpočet", "Budget"))}
      </div>
      <div className="relative mt-auto pt-6">{item(SettingsIcon, tr("Nastavení", "Settings"))}</div>
    </div>
  );
}

/** PageHeader + SummaryFigures, as every app page opens. */
function Header({
  back,
  title,
  sub,
  figures,
  stepper = true,
}: {
  back?: string;
  title: string;
  sub: string;
  figures: [string, string, "positive" | "negative" | "default"][];
  stepper?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        {back && (
          <p className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
            <ChevronLeft className="size-3" /> {back}
          </p>
        )}
        <p className="text-xl font-semibold tracking-tight">{title}</p>
        <p className="text-[13px] text-muted-foreground">{sub}</p>
        {stepper && (
          <div className="mt-2.5 inline-flex rounded-md border">
            <span className="flex size-7 items-center justify-center border-r text-muted-foreground">
              <ChevronLeft className="size-3.5" />
            </span>
            <span className="flex size-7 items-center justify-center text-muted-foreground">
              <ChevronRight className="size-3.5" />
            </span>
          </div>
        )}
      </div>
      <SummaryFigures className="gap-x-6 [&_.text-destructive]:text-red-700 dark:[&_.text-destructive]:text-red-400 [&_.text-emerald-600]:text-emerald-700 dark:[&_.text-emerald-600]:text-emerald-400 [&_dt+dd]:text-lg 2xl:[&_dt+dd]:text-xl">
        {figures.map(([label, value, tone]) => (
          <SummaryFigure key={label} label={label} value={value} tone={tone} />
        ))}
      </SummaryFigures>
    </div>
  );
}

function Progress({
  done,
  count,
  counts,
  locale,
}: {
  done: number;
  count: number;
  counts: Record<LandingStatus, number>;
  locale: LandingLocale;
}) {
  const tr = makeTr(locale);
  return (
    <div className="mt-5">
      <div className="flex items-baseline justify-between text-[13px] tabular-nums">
        <p>
          <span className="font-semibold">
            {tr(`Hotovo ${done} z ${count}`, `Done ${done} of ${count}`)}
          </span>
          <span className="text-muted-foreground">
            {" · "}
            {tr(`zbývá ${count - done}`, `${count - done} left`)}
          </span>
        </p>
        <p className="font-semibold">{Math.round((done / count) * 100)} %</p>
      </div>
      <StatusBar counts={counts} className="mt-2" />
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
        {KEYS.filter((k) => counts[k] > 0).map((k) => (
          <span key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <StatusChip status={k} locale={locale} />
            <span className="font-semibold tabular-nums text-foreground">{counts[k]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function MonthView({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  return (
    <>
      <Header
        back={tr("Přehled 2026", "Overview 2026")}
        title={tr("Září 2026", "September 2026")}
        sub={tr("20 transakcí", "20 transactions")}
        figures={[
          [tr("Příjmy", "Income"), "+74 200 Kč", "positive"],
          [tr("Výdaje", "Expenses"), "−61 845 Kč", "negative"],
          [tr("Saldo", "Net"), "+12 355 Kč", "default"],
        ]}
      />
      <Progress done={16} count={20} counts={SEPT_COUNTS} locale={locale} />
      <div className="mt-4 overflow-hidden rounded-xl border bg-card">
        <div className="flex items-center gap-2 border-b p-2.5">
          <span className="flex h-7 flex-1 items-center gap-2 rounded-md border px-2 text-xs text-muted-foreground sm:max-w-[12rem]">
            <Search className="size-3.5" /> {tr("Hledat…", "Search…")}
          </span>
          <span className="ml-auto hidden h-7 items-center rounded-md bg-[hsl(var(--action))] px-2.5 text-xs font-medium text-white dark:bg-primary dark:text-primary-foreground sm:flex">
            AI Match
          </span>
        </div>
        <div className="hidden grid-cols-[4.5rem_minmax(0,1fr)_7.5rem_7rem_9rem] gap-x-3 bg-secondary/50 px-3 py-2 text-xs font-medium text-muted-foreground lg:grid">
          <span>{tr("Datum", "Date")}</span>
          <span>{tr("Popis", "Description")}</span>
          <span className="text-right">{tr("Částka", "Amount")}</span>
          <span>{tr("Stav", "Status")}</span>
          <span>{tr("Doklad", "Document")}</span>
        </div>
        <div className="divide-y">
          {SEPTEMBER.map((tx) => (
            <div
              key={tx.date + tx.amount}
              className={cn(
                "border-l-2",
                tx.status === "unmatched" ? "border-l-transparent" : "",
                ROW_TINT[tx.status]
              )}
              style={
                tx.status !== "unmatched" ? { borderLeftColor: STATUSES[tx.status].bar } : undefined
              }
            >
              {/* One row for both widths: narrow stacks the chip under the text. */}
              <div className="grid grid-cols-[3.25rem_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2.5 text-[13px] lg:grid-cols-[4.5rem_minmax(0,1fr)_7.5rem_7rem_9rem]">
                <span className="tabular-nums text-muted-foreground lg:text-foreground">
                  {tx.date}
                </span>
                <span className="min-w-0">
                  <span className="block break-words font-medium lg:font-normal">
                    {pick(tx.text, locale)}
                  </span>
                  <StatusChip status={tx.status} locale={locale} className="mt-0.5 lg:hidden" />
                </span>
                <span
                  className={cn(
                    "whitespace-nowrap text-right font-mono tabular-nums",
                    !tx.amount.startsWith("−") && "text-emerald-600 dark:text-emerald-400"
                  )}
                >
                  {tx.amount}
                </span>
                <StatusChip status={tx.status} locale={locale} className="hidden lg:inline-flex" />
                <span className="hidden min-w-0 items-center gap-1.5 break-words text-[13px] lg:flex">
                  {tx.doc ? (
                    <>
                      <FileText
                        className={cn(
                          "size-3.5 shrink-0",
                          tx.status === "invoice"
                            ? "text-[hsl(var(--dot-blue))]"
                            : "text-[hsl(var(--action))] dark:text-primary"
                        )}
                      />
                      <span className="break-words">{tx.doc}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function YearView({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const months = locale === "cs" ? MONTHS.cs : MONTHS.en;
  const total = YEAR.reduce((n, m) => n + m.count, 0);
  const totals = toCounts(KEYS.map((_, i) => YEAR.reduce((n, m) => n + m.c[i], 0)));
  const done = totals.matched + totals.invoice + totals.notNeeded;
  return (
    <>
      <Header
        title={tr("Přehled 2026", "Overview 2026")}
        sub={tr(`${total} transakcí`, `${total} transactions`)}
        figures={[
          [tr("Příjmy", "Income"), "+954 000 Kč", "positive"],
          [tr("Výdaje", "Expenses"), "−635 000 Kč", "negative"],
          [tr("Saldo", "Net"), "+319 000 Kč", "default"],
        ]}
      />
      <Progress done={done} count={total} counts={totals} locale={locale} />
      <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4">
        {months.map((name, i) => {
          const m = YEAR[i];
          if (!m) {
            return (
              <div key={name} className="rounded-xl border border-dashed border-border/80 p-3.5">
                <p className="text-sm font-semibold tracking-tight text-muted-foreground/60">
                  {name}
                </p>
              </div>
            );
          }
          const counts = toCounts(m.c);
          const left = counts.missing + counts.unmatched;
          return (
            <MonthTileMock
              key={name}
              name={name}
              locale={locale}
              current={i === 9}
              counts={counts}
              net={m.net}
              percent={left === 0 ? null : `${Math.round(((m.count - left) / m.count) * 100)} %`}
              detail={
                left === 0
                  ? tr(`${m.count} transakcí`, `${m.count} transactions`)
                  : tr(
                      `${m.count} transakcí · zbývá ${left}`,
                      `${m.count} transactions · ${left} left`
                    )
              }
            />
          );
        })}
      </div>
    </>
  );
}

/** Last year's income, for the dashed comparison line (thousands of crowns). */
const PREV_INCOME = [85, 104, 99, 110, 96, 92, 80, 112, 88, 50, 96, 118];

/** Share of outgoing payments with a document, per month. */
const COVERAGE = [96, 92, 88, 100, 97, 85, 90, 94, 80, 58];

/** The new Analytika: period controls, year-on-year figures, cash-flow, coverage, counterparties. */
function AnalyticsView({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const short = locale === "cs" ? MONTHS_SHORT.cs : MONTHS_SHORT.en;
  const max = 150;
  const slot = 100 / 12;
  const prevLine = PREV_INCOME.map((v, i) => `${(i + 0.5) * slot},${100 - (v / max) * 100}`).join(
    " "
  );
  const coverageColor = (v: number) =>
    v === 100
      ? "bg-[hsl(var(--dot-green))]"
      : v >= 90
        ? "bg-[hsl(var(--dot-amber))]"
        : "bg-[hsl(var(--dot-red))]";
  const segment = (labels: string[], active: number) => (
    <div className="inline-flex rounded-lg bg-muted p-0.5 text-xs font-medium">
      {labels.map((l, i) => (
        <span
          key={l}
          className={cn(
            "rounded-md px-2.5 py-1",
            i === active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
          )}
        >
          {l}
        </span>
      ))}
    </div>
  );
  return (
    <>
      <div>
        <p className="text-xl font-semibold tracking-tight">{tr("Analytika", "Analytics")}</p>
        <p className="text-[13px] text-muted-foreground">
          {tr(
            "Moje firma s.r.o. · leden–říjen 2026 · přepočteno na Kč",
            "Moje firma s.r.o. · January–October 2026 · converted to CZK"
          )}
        </p>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {segment(
          [
            tr("Měsíc", "Month"),
            tr("Čtvrtletí", "Quarter"),
            tr("Rok", "Year"),
            tr("Vlastní", "Custom"),
          ],
          2
        )}
        <span className="inline-flex items-center rounded-md border text-xs font-semibold tabular-nums">
          <span className="flex size-7 items-center justify-center border-r text-muted-foreground">
            <ChevronLeft className="size-3.5" />
          </span>
          <span className="px-2.5">2026</span>
          <span className="flex size-7 items-center justify-center border-l text-muted-foreground">
            <ChevronRight className="size-3.5" />
          </span>
        </span>
        <span className="hidden items-center gap-2 text-xs font-medium sm:inline-flex">
          <span className="flex h-4 w-7 items-center justify-end rounded-full bg-[hsl(var(--action))] p-0.5 dark:bg-primary">
            <span className="size-3 rounded-full bg-white" />
          </span>
          {tr("Porovnat s 2025", "Compare with 2025")}
        </span>
      </div>
      <SummaryFigures className="mt-4 gap-x-8 [&_.text-destructive]:text-red-700 dark:[&_.text-destructive]:text-red-400 [&_.text-emerald-600]:text-emerald-700 dark:[&_.text-emerald-600]:text-emerald-400 [&_dt+dd]:text-lg 2xl:[&_dt+dd]:text-xl">
        <SummaryFigure
          label={tr("Příjmy", "Income")}
          value="954 000 Kč"
          tone="positive"
          sub={tr("▲ 4 % proti led–říj 2025", "▲ 4 % vs Jan–Oct 2025")}
        />
        <SummaryFigure
          label={tr("Výdaje", "Expenses")}
          value="635 000 Kč"
          tone="negative"
          sub={tr("▲ 6 % proti led–říj 2025", "▲ 6 % vs Jan–Oct 2025")}
        />
        <SummaryFigure
          label={tr("Saldo", "Net")}
          value="319 000 Kč"
          sub={tr("marže 33 %", "margin 33 %")}
        />
      </SummaryFigures>

      <div className="mt-4 rounded-xl border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">
              {tr("Cash-flow po měsících", "Cash flow by month")}
            </p>
            <p className="text-xs text-muted-foreground">
              {tr("Nejsilnější srpen: 126 000 Kč příjmů", "Strongest August: 126 000 Kč income")}
            </p>
          </div>
          {segment([tr("Graf", "Chart"), tr("Tabulka", "Table")], 0)}
        </div>
        <div className="relative mt-4 h-40">
          {[0, 50, 100, 150].map((v) => (
            <div
              key={v}
              className="absolute inset-x-0 border-t border-dashed border-border"
              style={{ bottom: `${(v / max) * 100}%` }}
            />
          ))}
          <div className="relative flex h-full items-end">
            {short.map((label, i) => {
              const [inc, exp] = TREND[i] ?? [0, 0];
              return (
                <div key={label} className="flex h-full flex-1 items-end justify-center gap-[2px]">
                  <div
                    className="w-[34%] max-w-4 rounded-t-[3px] bg-[#8ED17A]"
                    style={{ height: `${(inc / max) * 100}%` }}
                  />
                  <div
                    className="w-[34%] max-w-4 rounded-t-[3px] bg-[#F87171]"
                    style={{ height: `${(exp / max) * 100}%` }}
                  />
                </div>
              );
            })}
          </div>
          {/* Last year's income, dashed, as the app draws the comparison. */}
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            fill="none"
          >
            <polyline
              points={prevLine}
              stroke="hsl(var(--muted-foreground))"
              strokeWidth="1.5"
              strokeDasharray="4 3"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
        <div className="mt-1.5 flex">
          {short.map((label) => (
            <span key={label} className="flex-1 text-center text-[10px] text-muted-foreground">
              {label}
            </span>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-[2px] bg-[#8ED17A]" />
            {tr("Příjmy", "Income")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-[2px] bg-[#F87171]" />
            {tr("Výdaje", "Expenses")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 border-t-[1.5px] border-dashed border-muted-foreground" />
            {tr("Příjmy 2025", "Income 2025")}
          </span>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">
                {tr("Doklady k výdajům", "Documents for expenses")}
              </p>
              <p className="text-xs text-muted-foreground">
                {tr("Odchozí platby s dokladem", "Outgoing payments with a document")}
              </p>
            </div>
            <p className="text-xl font-semibold tabular-nums">88 %</p>
          </div>
          <div className="mt-3 flex items-end gap-1">
            {COVERAGE.map((v, i) => (
              <div key={short[i]} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex h-10 w-full items-end overflow-hidden rounded-[4px] bg-muted">
                  <div
                    className={cn("w-full rounded-[4px]", coverageColor(v))}
                    style={{ height: `${v}%` }}
                  />
                </div>
                <span className="text-[9px] text-muted-foreground">{short[i]}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs font-medium text-destructive dark:text-red-400">
            {tr("Chybí 9 dokladů", "9 documents missing")}
          </p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-sm font-semibold">{tr("Kam jdou peníze", "Where the money goes")}</p>
          <p className="text-xs text-muted-foreground">
            {tr("Největší protistrany", "Biggest counterparties")}
          </p>
          <div className="mt-3 space-y-2.5 text-[13px]">
            {(
              [
                [tr("Nájem kanceláře", "Office rent"), "185 000 Kč", 29],
                [tr("Účetní kancelář Brázda", "Brázda accounting"), "60 000 Kč", 9],
                ["Tiskárna Pixel", "41 600 Kč", 7],
              ] as [string, string, number][]
            ).map(([name, amount, share]) => (
              <div key={name}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="break-words">{name}</span>
                  <span className="whitespace-nowrap font-mono tabular-nums">{amount}</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-1.5 flex-1 rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-[#F87171]"
                      style={{ width: `${share * 2.5}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">
                    {share} %
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/** The interactive preview: tabs above a window with the sidebar and the chosen view. */
export function AppPreview({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const views: { id: View; label: string; describe: string; render: () => ReactNode }[] = [
    {
      id: "month",
      label: tr("Měsíc", "Month"),
      describe: tr(
        "Přehled září v Zettlu: příjmy, výdaje, pruh Hotovo 16 z 20 a platby s barevnými stavy. Vymyšlená firma.",
        "Zettl's September view: income, expenses, the Done 16 of 20 bar and payments with coloured statuses. Invented company."
      ),
      render: () => <MonthView locale={locale} />,
    },
    {
      id: "year",
      label: tr("Rok", "Year"),
      describe: tr(
        "Přehled roku: dlaždice měsíců se stavem párování a saldem. Vymyšlená data.",
        "The year overview: month tiles with matching progress and net. Invented data."
      ),
      render: () => <YearView locale={locale} />,
    },
    {
      id: "analytics",
      label: tr("Analytika", "Analytics"),
      describe: tr(
        "Analytika: příjmy, výdaje a saldo proti loňsku, cash-flow po měsících, doklady k výdajům a největší protistrany. Vymyšlená data.",
        "Analytics: income, expenses and net against last year, cash flow by month, documents for expenses and the biggest counterparties. Invented data."
      ),
      render: () => <AnalyticsView locale={locale} />,
    },
  ];

  return (
    <Tabs defaultValue="month" className="relative mt-12">
      <div
        aria-hidden="true"
        className="absolute -inset-x-10 -inset-y-6 -z-10"
        style={{
          background:
            "radial-gradient(ellipse at center, hsl(var(--primary) / 0.14), transparent 65%)",
        }}
      />
      {/* One app window: its title bar carries the view switch. */}
      <div className="overflow-hidden rounded-2xl border bg-background shadow-[0_24px_64px_-24px_rgb(0_0_0/0.35)] dark:shadow-[0_24px_80px_-24px_rgb(0_0_0/0.9)]">
        <div className="flex items-center gap-3 border-b bg-secondary/40 px-3 py-2 dark:bg-secondary/20">
          <span aria-hidden="true" className="hidden gap-1.5 sm:flex">
            {[0, 1, 2].map((d) => (
              <span key={d} className="size-2.5 rounded-full bg-muted-foreground/25" />
            ))}
          </span>
          <TabsList aria-label={tr("Ukázka aplikace", "App preview")} className="h-8">
            {views.map((v) => (
              <TabsTrigger
                key={v.id}
                value={v.id}
                className="px-3 text-[13px] focus-visible:ring-[hsl(var(--action))] dark:focus-visible:ring-primary"
              >
                {v.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
            {tr("Vymyšlená data", "Invented data")}
          </span>
        </div>
        {views.map((v) => (
          <TabsContent
            key={v.id}
            value={v.id}
            className="mt-0 focus-visible:ring-[hsl(var(--action))] dark:focus-visible:ring-primary"
          >
            <figure className="flex">
              <figcaption className="sr-only">{v.describe}</figcaption>
              <div aria-hidden="true" className="flex min-w-0 flex-1">
                <Sidebar view={v.id} locale={locale} />
                <div className="relative isolate min-w-0 flex-1 overflow-hidden p-4 sm:p-6">
                  {/* The app's page band (AppLayout), same classes. */}
                  <TopoTexture className="inset-x-0 top-0 -z-10 h-80 text-[hsl(var(--action))] opacity-[0.24] dark:text-primary dark:opacity-[0.13]" />
                  <div className="relative">{v.render()}</div>
                </div>
              </div>
            </figure>
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
}
